// src/store/slices/languageSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import i18n from '../../i18n';

// Get initial language from localStorage or i18next, defaulting to 'tr'
const getInitialLanguage = () => {
  try {
    const storedLanguage = localStorage.getItem('language');
    const i18nextLanguage = i18n.language;
    
    // Use stored language if available
    if (storedLanguage) {
      return storedLanguage;
    }
    
    // Otherwise use i18next language if available
    if (i18nextLanguage) {
      localStorage.setItem('language', i18nextLanguage);
      return i18nextLanguage;
    }
    
    // Default to Turkish
    return 'tr';
  } catch (error) {
    console.error('Error getting initial language:', error);
    return 'tr';
  }
};

// Async thunk for changing language
export const changeLanguage = createAsyncThunk(
  'language/changeLanguage',
  async (lang, { rejectWithValue }) => {
    try {
      await i18n.changeLanguage(lang);
      localStorage.setItem('language', lang);
      return lang;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  language: getInitialLanguage(),
  status: 'idle',
  error: null
};

const languageSlice = createSlice({
  name: 'language',
  initialState,
  reducers: {
    setLanguage: (state, action) => {
      const lang = action.payload;
      if (state.language !== lang) {
        state.language = lang;
        try {
          localStorage.setItem('language', lang);
          if (i18n.language !== lang) {
            i18n.changeLanguage(lang);
          }
        } catch (error) {
          console.error('Error persisting language:', error);
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(changeLanguage.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(changeLanguage.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.language = action.payload;
      })
      .addCase(changeLanguage.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  }
});

export const { setLanguage } = languageSlice.actions;

export const selectLanguage = (state) => state.language.language;

export default languageSlice.reducer;